// FunctionStrip — six big "what do you want to do?" buttons under the
// composer. Every mode (including Image and Agent) runs *inline* in the
// same MultiChat surface — no popouts, no route changes — so users
// never lose their place. Clicking a tile dispatches the kind for the
// next turn; MultiChat picks the right model and renderer.
import { MessageSquare, Image as ImageIcon, Globe, BarChart3, Code2, Bot } from "lucide-react";
import { defaultModelFor, type Capability } from "@/lib/aiModels";
import { cn } from "@/lib/utils";

type Fn = {
  key: "chat" | "research" | "analyze" | "code" | "image" | "agent";
  label: string;
  Icon: any;
  hint: string;
  capability: Capability;
  placeholder: string;
};

const FUNCTIONS: Fn[] = [
  { key: "chat",     capability: "chat",     label: "Chat",     Icon: MessageSquare, hint: "Ask anything",         placeholder: "Ask anything…" },
  { key: "research", capability: "research", label: "Research", Icon: Globe,         hint: "Live web + citations", placeholder: "Research a topic with live sources…" },
  { key: "analyze",  capability: "analyze",  label: "Analyze",  Icon: BarChart3,     hint: "Reason over data",     placeholder: "Paste data, docs, or numbers to analyze…" },
  { key: "code",     capability: "chat",     label: "Code",     Icon: Code2,         hint: "Write & debug code",   placeholder: "Describe code to write, fix, or explain…" },
  { key: "image",    capability: "image",    label: "Image",    Icon: ImageIcon,     hint: "Generate downloadable images", placeholder: "Describe an image to generate…" },
  { key: "agent",    capability: "agent",    label: "Agent",    Icon: Bot,           hint: "Build an agent inline", placeholder: "Describe an agent (what should it do?)…" },
];

// Code routes to a chat model but the strongest one available cheaply.
const CODE_MODEL_ID = "openai/gpt-5-mini";

export default function FunctionStrip() {
  const pick = (fn: Fn) => {
    // Pick the best default model for text kinds. Image/agent run on
    // a fixed surface so the modelId is informational only.
    const modelId =
      fn.key === "code"
        ? CODE_MODEL_ID
        : defaultModelFor(fn.capability).id;
    window.dispatchEvent(
      new CustomEvent("multichat-set-mode", {
        detail: { kind: fn.key, modelId, placeholder: fn.placeholder },
      }),
    );
  };

  return (
    <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2">
      {FUNCTIONS.map((fn) => {
        const Icon = fn.Icon;
        return (
          <button
            key={fn.key}
            type="button"
            onClick={() => pick(fn)}
            title={fn.hint}
            className={cn(
              "group flex flex-col items-center gap-1 py-3 px-2 rounded-xl",
              "border border-border bg-card/40 hover:border-primary/50 hover:bg-card",
              "text-muted-foreground hover:text-foreground transition-all",
            )}
          >
            <Icon className="w-4 h-4 text-primary/80 group-hover:text-primary" />
            <span className="text-[11px] font-mono">{fn.label}</span>
          </button>
        );
      })}
    </div>
  );
}